"use client";

import Link from "@/components/ui/LocaleLink";
import { writeDraft } from "@/lib/chat/contact-prefill";
import { ArrowRight } from "@phosphor-icons/react/dist/ssr";
import SparklesIcon from "@/components/ui/SparklesIcon";

/**
 * Suggested questions that open the assistant with the question already asked.
 * The hand-off is the mini bar's: `writeDraft(text, { auto: true })` BEFORE the
 * navigation, and `/chat` sends it on arrival (`ChatClient` → `takeDraft`).
 * ⚠️ The draft lives in sessionStorage, which is per tab, so these links must
 * NOT open a new tab, and a modified click (which does) writes nothing, so the
 * new tab opens an empty chat. Without JS they are plain links to `/chat`.
 */
export default function LandingAskList({ title, questions }: { title: string; questions: readonly string[] }) {
  return (
    <div className="rounded-card border border-card-line bg-white p-21">
      {/* The assistant's own glyph, as in the nav (owner, 2026-09-19). */}
      <p className="flex items-center gap-8 text-xs font-semibold uppercase tracking-widest text-navy">
        <SparklesIcon size={13} />
        {title}
      </p>
      <ul role="list" className="mt-13 grid gap-8">
        {questions.map((q) => (
          <li key={q}>
            <Link
              href="/chat"
              onClick={(e) => {
                // A modified click opens /chat in ANOTHER tab, which cannot read
                // this tab's sessionStorage; writing the draft anyway would leave
                // it here to auto-send on this tab's next /chat visit.
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                writeDraft(q, { auto: true });
              }}
              className="group flex items-start justify-between gap-8 rounded-control border border-card-line px-13 py-8 text-sm text-gray-600 transition-colors hover:border-navy hover:text-navy"
            >
              <span>{q}</span>
              <ArrowRight
                size={13}
                weight="bold"
                aria-hidden="true"
                className="mt-3 shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-4 group-focus-visible:translate-x-4"
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
